import { forward, sample } from 'effector';
import { jwtDecode } from 'libs/jwt-decode';
import { request, RequestException } from 'libs/request';
import { baseUrls$ } from 'model/config.model';
import { SessionConfig, SessionTokenPayload } from 'model/domain';
import { ErrorType, setError } from 'model/error.model';
import { Theme } from 'model/helpers/theme';
import { RootDomain } from 'model/root.model';
import { version } from '../../package.json';

export type Country = {
	icao_code: string;
	alpha2: string;
	alpha3: string;
	name: string;
	documents: Array<{ document_type: string }>
}
export type FetchSessionConfigPayload = {
	sessionToken: string;
	applicationKey: string;
}
export type Template = {
	id: string;
	name: string;
	default: boolean;
	version: number;
	hive_link: boolean;
	handoff_variants: string[];
	countries_whitelist: string[];
	countries_blacklist: string[];
	documents_whitelist: string[];
	created_at: number;
	updated_at: number;
}
export type FetchSessionConfigResult = {
	session_id: string,
	tenant: string,
	theme: Theme;
	template: Template;
	acceptedCountries: Country[];
	localization: {
		languageBaseUrl: string,
		languagesWhitelist: string[],
		supportedLanguages: { [key: string]: string },
	};
	hive_link: boolean;
	urls: {
		tos_url: string,
		support_url: string,
		handoff_url: string,
		hive_link_url: string,
	}
}

export const updateSessionToken = RootDomain.event<string>();
export const updateApplicationKey = RootDomain.event<string>();

export const sessionToken$ = RootDomain.store<string>(null);
export const applicationKey$ = RootDomain.store<string>(null);
export const sessionConfig$ = RootDomain.store<SessionConfig>(null);
export const template$ = RootDomain.store<Template>(null);
export const countries$ = RootDomain.store<Country[]>([]);

export const fetchSessionConfig = RootDomain
	.effect<FetchSessionConfigPayload, FetchSessionConfigResult, RequestException>();

fetchSessionConfig
	.use(async ({ sessionToken, applicationKey }) => {
		const payload: SessionTokenPayload = jwtDecode(sessionToken);
		return request({
			baseUrl: baseUrls$.getState().apiBaseUrl,
			url: `/sessions/${payload.session_id}/config`,
			headers: {
				'hive-application-key': applicationKey,
				'sdk-version': version,
			},
		});
	});

sample({
	source: fetchSessionConfig.fail,
	fn: (payload) => {
		if (payload.error.status === 400) {
			return ErrorType.invalid_session;
		} else {
			return ErrorType.unhandled;
		}
	},
	target: setError,
});

countries$
	.on(fetchSessionConfig.doneData, (_, payload) => payload.acceptedCountries);
applicationKey$
	.on(updateApplicationKey, (_, key) => key);
sessionToken$
	.on(updateSessionToken, (_, token) => token);
sessionConfig$
	.on(fetchSessionConfig.doneData, (_, result) => ({
		id: result.session_id,
		company: result.tenant,
		hive_link: result.hive_link,
		urls: result.urls,
	}));
template$
	.on(fetchSessionConfig.doneData, (_, result) => result.template);

sample({
	source: { sessionToken: sessionToken$, applicationKey: applicationKey$ },
	clock: updateSessionToken,
	target: fetchSessionConfig,
});
forward({
	from: fetchSessionConfig.fail,
	to: setError.prepend(() => ErrorType.unhandled),
});
